import React, { useEffect, useState } from 'react';
import { Flex, Div, Gutter, Title, BodyText, Table, GridBag, Loader } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { Country, FinanceProduct } from '@cdaas/api-clients/dist/product-service-openapi';

import { Errors, TeliaFinanceGuidChangeParam } from '../utils';
import FinanceProductIdRow from './FinanceProductIdRow';
import { productServiceAdminTeliaFinanceProductsClient } from '../../../../api-client/product-service-client';

interface FinanceProductIdsProps {
  // Map of Country id to Telia Finance productId - one per country where finance id has been set for the product
  financeIdsPerCountry: { [key: string]: string };
  // Changing finance id of country currently means changing it for all variants of the product
  handleVariantsTeliaFinanceGUIDChange: (financeProductId: TeliaFinanceGuidChangeParam) => void;
  countries: Country[];
  errors: Errors['financeId'];
}

function FinanceProductIds(props: FinanceProductIdsProps) {
  const { countries, financeIdsPerCountry, handleVariantsTeliaFinanceGUIDChange } = props;
  const { t } = useTranslation('productscreen');
  const [teliaFinanceProducts, setTeliaFinanceProducts] = useState<FinanceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  useEffect(() => {
    const fetchTeliaFinanceProducts = async () => {
      try {
        const response = await productServiceAdminTeliaFinanceProductsClient.getFinanceProducts();
        setTeliaFinanceProducts(response.data.products);
      } catch {
        setApiError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchTeliaFinanceProducts();
  }, []);

  const render = (): JSX.Element => {
    let content;

    if (loading) {
      content = (
        <Div alignTextToCenter>
          <Loader size="lg" />
        </Div>
      );
    } else if (apiError) {
      content = (
        <BodyText size="sm" color="red600">
          {t('tfProductIdSection.errorGettingFinanceProductList')}
        </BodyText>
      );
    } else {
      // Telia Finance Product IDs table
      content = (
        <GridBag>
          {/* Adjust the size of the table based on the number of columns */}
          <GridBag.Item md={10}>
            <Table className="product-variants-details__variants-table">
              <thead>
                <Table.Row>
                  <Table.HeaderCell>
                    <Div>
                      <BodyText fontWeight="bold">{t('tfProductIdSection.country')}</BodyText>
                    </Div>
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    <Div>
                      <BodyText fontWeight="bold">{t('tfProductIdSection.financeProductName')}</BodyText>
                    </Div>
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    <Div>
                      <BodyText fontWeight="bold">{t('tfProductIdSection.financeProductId')}</BodyText>
                    </Div>
                  </Table.HeaderCell>
                </Table.Row>
              </thead>
              <tbody>
                {(countries || []).map((country: Country) => (
                  <FinanceProductIdRow
                    key={country.code}
                    country={country}
                    financeProductIds={teliaFinanceProducts}
                    currentlySetFinanceProductId={financeIdsPerCountry[country.id.toString()]}
                    handleFinanceProductIdChange={handleVariantsTeliaFinanceGUIDChange}
                  />
                ))}
              </tbody>
            </Table>
          </GridBag.Item>
        </GridBag>
      );
    }

    return (
      <Div>
        <Flex directionColumn>
          <Title size="md" padding={{ right: 'lg' }}>
            {t('tfProductIdSection.title')}
          </Title>
          <Gutter />
          <BodyText color="gray400">{t('tfProductIdSection.guideText')}</BodyText>
          <Gutter />
          {content}
        </Flex>
      </Div>
    );
  };

  return render();
}

export default React.memo(FinanceProductIds);
